/**
 * <mini-gallery> — snippets/mini-gallery.liquid. Loaded by what renders one:
 * the quick add's content (assets/quick-add.js loads the scripts it carries)
 * and the compare table (snippets/product-compare.liquid's resources).
 */
if (!customElements.get('mini-gallery')) {
  /**
   * A product's images one at a time (ADR-0024, ADR-0025). The arrows step
   * through them, a thumbnail shows its own, and `show()` shows a variant's
   * image. The thumbnails, the arrows and the counter always follow the image
   * shown: the arrow at either end is disabled, as on the product page.
   */
  class MiniGallery extends theme.BaseElement {
    connectedCallback() {
      this.main = this.querySelector('.mini-gallery__main');
      const data = this.querySelector('.mini-gallery__data');
      this.media = data ? JSON.parse(data.textContent) : [];
      this.on(this, 'click', (evt) => this.handleClick(evt));
      if (this.main) this.mark(this.main.dataset.mediaId);
    }

    /**
     * @param {MouseEvent} evt
     */
    handleClick(evt) {
      const arrow = evt.target.closest('.media-ctrl__btn');
      const thumb = evt.target.closest('.mini-gallery__thumb');
      if (arrow) {
        const next = this.media[this.index() + (arrow.name === 'next' ? 1 : -1)];
        if (next) this.show(next.id);
      } else if (thumb) {
        this.show(thumb.dataset.mediaId);
      }
    }

    /** @returns {number} The position of the image shown, -1 if it is not one of the product's. */
    index() {
      return this.media.findIndex((item) => `${item.id}` === this.main.dataset.mediaId);
    }

    /**
     * Shows one of the product's images by id, or a media object such as a
     * variant's `featured_media`.
     * @param {string|number|object} media
     */
    show(media) {
      if (!this.main || media == null) return;
      const item =
        typeof media === 'object'
          ? { id: media.id, src: media.preview_image.src, aspect_ratio: media.preview_image.aspect_ratio, alt: media.alt }
          : this.media.find((entry) => `${entry.id}` === `${media}`);
      if (!item) return;

      const existing = this.main.querySelector('img');
      if (!existing || this.main.dataset.mediaId !== `${item.id}`) {
        // The classes the template gave the first image (the product page's,
        // per the crop setting) carry over to every image shown after it.
        const width = this.main.offsetWidth || 600;
        const sized = (w) => `${item.src}${item.src.includes('?') ? '&' : '?'}width=${w}`;
        const img = document.createElement('img');
        img.className = existing ? existing.className : 'product-image img-fit img-fit--contain w-full';
        img.src = sized(width);
        img.srcset = `${sized(width)}, ${sized(width * 2)} 2x`;
        img.width = width * 2;
        img.height = Math.round((width * 2) / item.aspect_ratio);
        img.alt = item.alt || '';
        this.main.replaceChildren(img);
        this.main.dataset.mediaId = String(item.id);
      }
      this.mark(item.id);
    }

    /**
     * Marks the thumbnail of the image shown and scrolls it into its row, and
     * sets the arrows and the counter to its position.
     * @param {string|number} mediaId
     */
    mark(mediaId) {
      const index = this.media.findIndex((item) => `${item.id}` === `${mediaId}`);

      this.querySelectorAll('.mini-gallery__thumb').forEach((thumb) => {
        const current = thumb.dataset.mediaId === `${mediaId}`;
        thumb.classList.toggle('is-active', current);
        if (current) {
          thumb.setAttribute('aria-current', 'true');
          const item = thumb.closest('li');
          const row = item.parentElement;
          row.scrollTo({ left: item.offsetLeft - (row.clientWidth - item.offsetWidth) / 2, behavior: 'smooth' });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      const prev = this.querySelector('.media-ctrl__btn[name="prev"]');
      const next = this.querySelector('.media-ctrl__btn[name="next"]');
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index === -1 || index >= this.media.length - 1;
      const counter = this.querySelector('.media-ctrl__current-item');
      if (counter && index > -1) counter.textContent = index + 1;
    }
  }

  customElements.define('mini-gallery', MiniGallery);
}
